import {Injectable, OnDestroy} from '@angular/core';
import {Store} from '@ngrx/store';
import {Observable, Subject, interval, Subscription} from 'rxjs';
import {takeUntil, distinctUntilChanged} from 'rxjs/operators';
import {BackgroundTask} from './background-task.model';
import {BackgroundTaskActions} from './background-task.actions';
import {BackgroundTaskSelectors} from './background-task.selectors';

@Injectable({providedIn: 'root'})
export class BackgroundTaskFacade implements OnDestroy {
  private destroy$ = new Subject<void>();
  private pollingSubscription: Subscription | null = null;
  private hasPendingSubscription: Subscription | null = null;
  private pollingIntervalMs = 5000;

  pendingTasks$: Observable<BackgroundTask[]> = this.store.select(BackgroundTaskSelectors.selectPendingTasks);
  pendingTaskCount$: Observable<number> = this.store.select(BackgroundTaskSelectors.selectPendingTaskCount);
  hasPendingTasks$: Observable<boolean> = this.store.select(BackgroundTaskSelectors.selectHasPendingTasks);
  allTasks$: Observable<BackgroundTask[]> = this.store.select(BackgroundTaskSelectors.selectAllTasks);
  completedNotifications$: Observable<BackgroundTask[]> = this.store.select(BackgroundTaskSelectors.selectCompletedNotifications);
  isLoading$: Observable<boolean> = this.store.select(BackgroundTaskSelectors.selectIsLoading);

  constructor(private store: Store) {}

  ngOnDestroy(): void {
    this.stopPolling();
  }

  loadPendingTasks(): void {
    this.store.dispatch(BackgroundTaskActions.loadPendingTasks());
  }

  loadTasks(limit?: number): void {
    this.store.dispatch(BackgroundTaskActions.loadTasks({limit}));
  }

  clearCompletedNotification(taskId: number): void {
    this.store.dispatch(BackgroundTaskActions.clearCompletedNotification({taskId}));
  }

  startPolling(intervalMs: number = 5000): void {
    if (this.hasPendingSubscription) return;
    this.pollingIntervalMs = intervalMs;

    // Do initial check
    this.loadPendingTasks();

    // Subscribe to hasPendingTasks to manage polling
    this.hasPendingSubscription = this.hasPendingTasks$.pipe(
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(hasTasks => {
      if (hasTasks) {
        this.startActivePolling();
      } else {
        this.stopActivePolling();
      }
    });
  }

  // Call this after triggering a background task (e.g., clone)
  triggerCheck(): void {
    this.loadPendingTasks();
  }

  private startActivePolling(): void {
    if (this.pollingSubscription) return;

    this.pollingSubscription = interval(this.pollingIntervalMs).pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.loadPendingTasks();
    });
  }

  private stopActivePolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
  }

  stopPolling(): void {
    this.stopActivePolling();
    if (this.hasPendingSubscription) {
      this.hasPendingSubscription.unsubscribe();
      this.hasPendingSubscription = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
    this.destroy$ = new Subject<void>();
  }
}
